// src/components/switches/SwitchForm.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Stack,
  IconButton,
  useTheme,
  Autocomplete,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Close, SwapHoriz, Business, Event, EventBusy, Save, LocationOn, ElectricBolt, LocalFireDepartment } from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { createSwitch, SUPPLY_TYPES } from "../../utils/models/switchModel";
import { CLIENT_TAGS } from "../../utils/models/clientModel";
import { SWITCH_STATUS } from "../../utils/models/switchClientModel";

const SwitchForm = ({ switchItem, onSave, onCancel }) => {
  const theme = useTheme();
  const { add, update } = useAuditFirestore("switches");
  const { update: updateClient } = useAuditFirestore("clients");
  const { data: clients } = useCollection("clients");
  const { data: suppliers } = useCollection("switchSuppliers");
  const { data: switches } = useCollection("switches");
  
  const [formData, setFormData] = useState(createSwitch({}));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const switchClients = clients?.filter(
    (c) => c.isActive !== false && c.tags?.includes(CLIENT_TAGS.CAMBIO_COMERCIALIZADORA)
  ) || [];

  const activeSuppliers = suppliers?.filter((s) => s.isActive !== false) || [];

  const selectedClient = formData.switchClientId
    ? switchClients.find((c) => c.id === formData.switchClientId) || null
    : null;
  
  const selectedSupplier = formData.supplierId
    ? activeSuppliers.find((s) => s.id === formData.supplierId) || null
    : null;

  // Obtener las direcciones del cliente seleccionado
  const clientAddresses = selectedClient?.addresses || [];

  useEffect(() => {
    if (switchItem) {
      setFormData(switchItem);
    } else {
      setFormData(createSwitch({}));
    }
  }, [switchItem]);

  // Auto-seleccionar dirección si solo hay una
  useEffect(() => {
    if (selectedClient && clientAddresses.length === 1 && !formData.id) {
      const addr = clientAddresses[0];
      setFormData((prev) => ({
        ...prev,
        selectedAddress: 0,
        addressStreet: `${addr.street || ""}, ${addr.city || ""}, ${addr.state || ""}`.trim(),
        addressCity: addr.city || "",
        addressCups: addr.cups || "",
        addressCupsGas: addr.cupsGas || "",
        addressReferenciaCatastral: addr.referenciaCatastral || "",
      }));
    }
  }, [selectedClient, clientAddresses.length]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSupplyTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setFormData((prev) => ({ ...prev, supplyType: newValue }));
    }
  };

  const handleAddressChange = (event) => {
    const index = event.target.value;
    const addr = clientAddresses[index];
    
    if (addr) {
      setFormData((prev) => ({
        ...prev,
        selectedAddress: index,
        addressStreet: `${addr.street || ""}, ${addr.city || ""}, ${addr.state || ""}`.trim(),
        addressCity: addr.city || "",
        addressCups: addr.cups || "",
        addressCupsGas: addr.cupsGas || "",
        addressReferenciaCatastral: addr.referenciaCatastral || "",
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.switchClientId) newErrors.switchClientId = "Selecciona un cliente";
    if (!formData.supplierId) newErrors.supplierId = "Selecciona una comercializadora";
    if (!formData.fechaEntrada) newErrors.fechaEntrada = "La fecha de entrada es obligatoria";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateClientStatus = async (clientId) => {
    const clientSwitches = switches?.filter(
      (s) => s.switchClientId === clientId && s.isActive !== false
    ) || [];

    const otherSwitches = switchItem
      ? clientSwitches.filter((s) => s.id !== switchItem.id)
      : clientSwitches;

    const hasActiveSwitch = otherSwitches.some((s) => !s.fechaSalida);
    const allHaveExitDate = otherSwitches.length > 0 && otherSwitches.every((s) => s.fechaSalida);

    let newStatus;
    if (hasActiveSwitch || !formData.fechaSalida) {
      newStatus = SWITCH_STATUS.ACTIVE;
    } else if (allHaveExitDate && formData.fechaSalida) {
      newStatus = SWITCH_STATUS.DESERTER;
    } else {
      newStatus = SWITCH_STATUS.PENDING;
    }

    try {
      await updateClient(clientId, {
        switchStatus: newStatus,
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error updating client status:", error);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const data = {
        ...formData,
        fechaEntrada: formData.fechaEntrada,
        fechaSalida: formData.fechaSalida || '',
        updatedAt: new Date().toISOString(),
      };

      if (formData.id) {
        await update(formData.id, data);
      } else {
        const { id, ...dataWithoutId } = data;
        dataWithoutId.createdAt = new Date().toISOString();
        await add(dataWithoutId);
      }

      await updateClientStatus(formData.switchClientId);
      onSave();
    } catch (error) {
      console.error("Error saving switch:", error);
    } finally {
      setLoading(false);
    }
  };

  // Formatear dirección para mostrar
  const formatAddress = (addr) => {
    if (!addr) return "";
    const parts = [];
    if (addr.street) parts.push(addr.street);
    if (addr.city) parts.push(addr.city);
    if (addr.state) parts.push(addr.state);
    if (addr.zipCode) parts.push(addr.zipCode);
    return parts.join(", ");
  };

  // Obtener CUPS según tipo de suministro
  const getCupsByType = (addr) => {
    if (!addr) return "";
    return formData.supplyType === SUPPLY_TYPES.GAS ? addr.cupsGas || "" : addr.cups || "";
  };

  return (
    <Box>
      {/* Header */}
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SwapHoriz sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {formData.id ? "Editar Trámite" : "Nuevo Trámite"}
          </Typography>
        </Box>
        <IconButton onClick={onCancel} size="small"><Close /></IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ px: 3, pt: 4, pb: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        
        {/* TIPO DE SUMINISTRO - LUZ O GAS */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: theme.palette.text.primary }}>
            Tipo de suministro
          </Typography>
          <ToggleButtonGroup
            value={formData.supplyType}
            exclusive
            onChange={handleSupplyTypeChange}
            size="medium"
            fullWidth
            sx={{
              "& .MuiToggleButton-root": {
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                py: 1.5,
                border: "2px solid",
                borderColor: theme.palette.divider,
                "&.Mui-selected": {
                  backgroundColor: formData.supplyType === SUPPLY_TYPES.LUZ
                    ? `${theme.palette.warning.main}15`
                    : `${theme.palette.error.main}15`,
                  borderColor: formData.supplyType === SUPPLY_TYPES.LUZ
                    ? theme.palette.warning.main
                    : theme.palette.error.main,
                  color: formData.supplyType === SUPPLY_TYPES.LUZ
                    ? theme.palette.warning.main
                    : theme.palette.error.main,
                },
              },
            }}
          >
            <ToggleButton value={SUPPLY_TYPES.LUZ} sx={{ flex: 1, gap: 1 }}>
              <ElectricBolt />
              Luz
            </ToggleButton>
            <ToggleButton value={SUPPLY_TYPES.GAS} sx={{ flex: 1, gap: 1 }}>
              <LocalFireDepartment />
              Gas
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Selector de Cliente */}
        <Autocomplete
          key={`client-${formData.switchClientId}`}
          options={switchClients}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return option.type === "company"
              ? option.companyName
              : `${option.firstName} ${option.lastName}`;
          }}
          value={selectedClient}
          onChange={(event, newValue) => {
            setFormData((prev) => ({
              ...prev,
              switchClientId: newValue?.id || "",
              switchClientName: newValue
                ? newValue.type === "company"
                  ? newValue.companyName
                  : `${newValue.firstName} ${newValue.lastName}`
                : "",
              // Resetear dirección al cambiar de cliente
              selectedAddress: null,
              addressStreet: "",
              addressCity: "",
              addressCups: "",
              addressCupsGas: "",
              addressReferenciaCatastral: "",
            }));
            if (errors.switchClientId) setErrors((prev) => ({ ...prev, switchClientId: "" }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cliente"
              placeholder="Selecciona un cliente..."
              error={!!errors.switchClientId}
              helperText={errors.switchClientId}
            />
          )}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          noOptionsText="No hay clientes con cambio de comercializadora"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />

        {/* Selector de Dirección - Solo si hay cliente seleccionado */}
        {selectedClient && clientAddresses.length > 0 && (
          <FormControl fullWidth size="medium">
            <InputLabel>Dirección de suministro</InputLabel>
            <Select
              value={formData.selectedAddress !== null && formData.selectedAddress !== undefined ? formData.selectedAddress : ""}
              onChange={handleAddressChange}
              label="Dirección de suministro"
              sx={{ borderRadius: "12px" }}
              startAdornment={
                <InputAdornment position="start">
                  <LocationOn sx={{ color: theme.palette.warning.main, ml: 1 }} />
                </InputAdornment>
              }
            >
              {clientAddresses.map((addr, index) => (
                <MenuItem key={index} value={index}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {formatAddress(addr) || `Dirección ${index + 1}`}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {addr.cups && (
                        <Typography variant="caption" color="warning.main" sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                          <ElectricBolt sx={{ fontSize: 12 }} />
                          {addr.cups}
                        </Typography>
                      )}
                      {addr.cupsGas && (
                        <Typography variant="caption" color="error.main" sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                          <LocalFireDepartment sx={{ fontSize: 12 }} />
                          {addr.cupsGas}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Info de la dirección seleccionada */}
        {selectedClient && formData.selectedAddress !== null && formData.selectedAddress !== undefined && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "12px",
              bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : `${theme.palette.warning.main}08`,
              border: "1px solid",
              borderColor: `${theme.palette.warning.main}20`,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Datos del suministro seleccionado ({formData.supplyType === SUPPLY_TYPES.GAS ? "Gas" : "Luz"}):
            </Typography>
            <Stack spacing={1}>
              {formData.addressStreet && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOn sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                  <Typography variant="body2">{formData.addressStreet}</Typography>
                </Box>
              )}
              {formData.supplyType === SUPPLY_TYPES.LUZ && formData.addressCups && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ElectricBolt sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>CUPS Luz:</strong> {formData.addressCups}
                  </Typography>
                </Box>
              )}
              {formData.supplyType === SUPPLY_TYPES.GAS && formData.addressCupsGas && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocalFireDepartment sx={{ fontSize: 16, color: theme.palette.error.main }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>CUPS Gas:</strong> {formData.addressCupsGas}
                  </Typography>
                </Box>
              )}
              {formData.addressReferenciaCatastral && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Ref. Catastral:</strong> {formData.addressReferenciaCatastral}
                </Typography>
              )}
            </Stack>
          </Paper>
        )}

        {/* Selector de Comercializadora */}
        <Autocomplete
          key={`supplier-${formData.supplierId}`}
          options={activeSuppliers}
          getOptionLabel={(option) => option?.name || ""}
          value={selectedSupplier}
          onChange={(event, newValue) => {
            setFormData((prev) => ({
              ...prev,
              supplierId: newValue?.id || "",
              supplierName: newValue?.name || "",
            }));
            if (errors.supplierId) setErrors((prev) => ({ ...prev, supplierId: "" }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Comercializadora"
              placeholder="Selecciona una comercializadora..."
              error={!!errors.supplierId}
              helperText={errors.supplierId}
            />
          )}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          noOptionsText="No hay comercializadoras disponibles"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />

        {/* Fecha de Entrada */}
        <TextField
          fullWidth
          type="date"
          label="Fecha de Entrada"
          value={formData.fechaEntrada}
          onChange={handleChange("fechaEntrada")}
          error={!!errors.fechaEntrada}
          helperText={errors.fechaEntrada}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Event sx={{ color: errors.fechaEntrada ? theme.palette.error.main : theme.palette.success.main }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />

        {/* Fecha de Salida */}
        <TextField
          fullWidth
          type="date"
          label="Fecha de Salida (opcional)"
          value={formData.fechaSalida}
          onChange={handleChange("fechaSalida")}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EventBusy sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
          helperText="Dejar vacío si el trámite sigue activo"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 2.5, borderTop: "1px solid", borderColor: theme.palette.divider, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, px: 3 }}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={<Save />} sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, px: 4, boxShadow: "none" }}>
          {loading ? "Guardando..." : formData.id ? "Actualizar" : "Crear Trámite"}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default SwitchForm;